import { browser } from '$app/environment';
import { getGame, getGameId } from '@infinitro/catalog-min';
import { getRealm, stateRealm } from '@infinitro/config';
import { get, writable } from 'svelte/store';
import { guestStorage, playerStorage } from './storageGames';
export var Api;
(function (Api) {
    Api["chat"] = "chat";
    Api["notify"] = "notify";
    Api["player"] = "player";
    Api["public"] = "public";
})(Api || (Api = {}));
let realm = getRealm();
stateRealm.subscribe((value) => (realm = value));
export const recentlyPlayedGames = writable([]);
export const favoriteGames = writable([]);
export const likedGames = writable([]);
// Declare variables for API functions
let AsyncGetMyGames;
let FavoriteGame;
let LikeGame;
let PutMyGames;
let GetMyGamesDoc;
export const getFavoritesLikedAndRecentlyPlayed = async (opts = { realm: realm }, loggedIn) => {
    // const favoriteResults = await Promise.all(
    // 	catalog.map((game: GameInfo) => isFavoriteGame(game, opts, loggedIn))
    // );
    // const likedResults = await Promise.all(
    // 	catalog.map((game: GameInfo) => isLikedGame(game, opts, loggedIn))
    // );
    // const recentlyPlayedResults = await Promise.all(
    // 	catalog.map((game: GameInfo) => isRecentlyPlayed(game, opts, loggedIn))
    // );
    // const favoriteGames = catalog.filter((_, index) => favoriteResults[index]);
    // const likedGames = catalog.filter((_, index) => likedResults[index]);
    // const recentlyPlayedGames = catalog.filter((_, index) => recentlyPlayedResults[index]);
    const result = {
        favoriteGames: [],
        likedGames: [],
        recentlyPlayedGames: []
    };
    return result;
};
// Select the correct storage wrapper and identifier.
function getStorageAndIdentifier(opts) {
    if (opts.playerId) {
        return { storage: playerStorage, identifier: opts.playerId };
    }
    else {
        const realmString = opts.realm || realm;
        if (!realmString) {
            throw new Error("A 'realm' is required for guest users or a 'playerId' for registered users.");
        }
        return { storage: guestStorage, identifier: realmString };
    }
}
// Check if a game is in favorites.
export const isFavoriteGame = async (game, opts, loggedIn) => {
    if (loggedIn) {
        // Logged-in users: check the Svelte store favoriteGames
        const currentFavorites = get(favoriteGames); // GameInfo[]
        const result = currentFavorites.some((fav) => fav.provider_slug === game.provider_slug && fav.game_slug === game.game_slug);
        return result;
    }
    else {
        // Guests / not logged in: check local storage
        const { storage, identifier } = getStorageAndIdentifier(opts);
        const stored = await storage.getItem('favorite_games', identifier);
        if (stored) {
            const list = stored.map((s) => s.trim()).filter((s) => s);
            return list.includes(getGameId(game));
        }
        return false;
    }
};
// Check if a game is liked.
export const isLikedGame = async (game, opts, loggedIn) => {
    if (loggedIn) {
        // Logged-in users: check the Svelte store likedGames
        const currentLikes = get(likedGames); // GameInfo[]
        return currentLikes.some((liked) => liked.provider_slug === game.provider_slug && liked.game_slug === game.game_slug);
    }
    else {
        // Guests / not logged in: check local storage
        const { storage, identifier } = getStorageAndIdentifier(opts);
        const stored = await storage.getItem('liked_games', identifier);
        if (stored) {
            const list = stored.map((s) => s.trim()).filter((s) => s);
            return list.includes(getGameId(game));
        }
        return false;
    }
};
// Check if a game was recently played.
export const isRecentlyPlayed = async (game, opts, loggedIn) => {
    if (loggedIn) {
        // Logged-in users: check the Svelte store recentlyPlayedGames
        const currentRecent = get(recentlyPlayedGames); // GameInfo[]
        return currentRecent.some((rp) => rp.provider_slug === game.provider_slug && rp.game_slug === game.game_slug);
    }
    else {
        // Guests / not logged in: check local storage
        const { storage, identifier } = getStorageAndIdentifier(opts);
        const stored = await storage.getItem('recently_played', identifier);
        if (stored) {
            const list = stored.map((s) => s.trim()).filter((s) => s);
            return list.includes(getGameId(game));
        }
        return false;
    }
};
// Retrieve the favorite games list.
export const getFavoriteGames = async (opts) => {
    const { storage, identifier } = getStorageAndIdentifier(opts);
    const stored = await storage.getItem('favorite_games', identifier);
    return stored ? stored.map((s) => s.trim()).filter((s) => s) : [];
};
// Retrieve the liked games list.
export const getLikedGames = async (opts) => {
    const { storage, identifier } = getStorageAndIdentifier(opts);
    const stored = await storage.getItem('liked_games', identifier);
    return stored ? stored.map((s) => s.trim()).filter((s) => s) : [];
};
// Retrieve the recently played games list.
export const getRecentlyPlayed = async (opts) => {
    const { storage, identifier } = getStorageAndIdentifier(opts);
    const stored = await storage.getItem('recently_played', identifier);
    return stored ? stored.map((s) => s.trim()).filter((s) => s) : [];
};
async function convertRefsToGameInfo(refs) {
    const result = [];
    for (const ref of refs) {
        const [provSlug, gameSlug] = ref.split('#');
        const game = await getGame(provSlug, gameSlug);
        if (game) {
            result.push(game);
        }
    }
    return result;
}
const importAPIModule = async () => {
    if (browser && !AsyncGetMyGames) {
        const module = await import('./api/api');
        AsyncGetMyGames = module.AsyncGetMyGames;
        FavoriteGame = module.FavoriteGame;
        LikeGame = module.LikeGame;
        PutMyGames = module.PutMyGames;
    }
};
importAPIModule();
export async function initGamesState(loggedIn, opts) {
    if (!browser)
        return;
    if (!loggedIn) {
        // 2) Load local arrays of "providerSlug#gameSlug" from storage
        const favoriteIds = (await guestStorage.getItem('favorite_games', opts?.realm ?? '')) || [];
        const likedIds = (await guestStorage.getItem('liked_games', opts?.realm ?? '')) || [];
        const recentIds = (await guestStorage.getItem('recently_played', opts?.realm ?? '')) || [];
        // 3) Convert each "providerSlug#gameSlug" into full GameInfo
        const favoriteInfos = await convertRefsToGameInfo(favoriteIds);
        const likedInfos = await convertRefsToGameInfo(likedIds);
        const recentInfos = await convertRefsToGameInfo(recentIds);
        // 4) Set the Svelte stores
        favoriteGames.set(favoriteInfos);
        likedGames.set(likedInfos);
        recentlyPlayedGames.set(recentInfos);
    }
    else {
        //----- LOGGED-IN -----
        await refreshGamesState();
    }
}
export async function refreshGamesState() {
    if (!browser || !AsyncGetMyGames)
        return;
    await importAPIModule();
    try {
        const res = await AsyncGetMyGames({
            fetchPolicy: 'network-only',
            context: { api: Api.player, headers: { 'Cache-Control': 'no-cache' } }
        });
        const data = res.data?.getMyGames;
        if (data) {
            const recentInfos = await convertRefsToGameInfo(data.recent?.split(',') || []);
            const favoriteInfos = await convertRefsToGameInfo(data.favorites?.split(',') || []);
            const likedInfos = await convertRefsToGameInfo(data.likes?.split(',') || []);
            recentlyPlayedGames.set(recentInfos);
            favoriteGames.set(favoriteInfos);
            likedGames.set(likedInfos);
        }
    }
    catch (err) {
        console.log('Error refreshing games state:', err);
    }
}
export async function putMyGames(favorites, likes, loggedIn) {
    if (!loggedIn) {
        console.log('Guest users cannot update games.');
        return;
    }
    if (!browser || !PutMyGames)
        return;
    try {
        const res = await PutMyGames({
            variables: { favorites, likes },
            context: { api: Api.player }
        });
        await refreshGamesState();
        return res;
    }
    catch (error) {
        console.log('Error updating games:', error);
    }
}
export async function likeGame(providerSlug, gameSlug, loggedIn) {
    if (!loggedIn) {
        const game = `${providerSlug}#${gameSlug}`;
        guestStorage.addGame('liked_games', game, realm);
        const gameInfo = await getGame(providerSlug, gameSlug);
        if (gameInfo) {
            likedGames.update((current) => {
                // If it’s already in the store, remove it; otherwise, add it
                const index = current.findIndex((g) => g.provider_slug === providerSlug && g.game_slug === gameSlug);
                if (index >= 0) {
                    // remove it
                    const newList = [...current];
                    newList.splice(index, 1);
                    return newList;
                }
                else {
                    // add it
                    return [...current, gameInfo];
                }
            });
        }
        return;
    }
    if (!browser || !LikeGame)
        return;
    try {
        const alreadyLiked = get(likedGames).some((g) => g.provider_slug === providerSlug && g.game_slug === gameSlug);
        const res = await LikeGame({
            variables: { providerSlug, gameSlug, unlike: alreadyLiked },
            context: { api: Api.player },
            refetchQueries: [{ query: GetMyGamesDoc }]
        });
        await refreshGamesState();
        return res;
    }
    catch (error) {
        console.log('Error liking game:', error);
    }
}
export async function favoriteGame(providerSlug, gameSlug, loggedIn) {
    if (!loggedIn) {
        const game = `${providerSlug}#${gameSlug}`;
        guestStorage.addGame('favorite_games', game, realm);
        const gameInfo = await getGame(providerSlug, gameSlug);
        if (gameInfo) {
            favoriteGames.update((current) => {
                const index = current.findIndex((g) => g.provider_slug === providerSlug && g.game_slug === gameSlug);
                if (index >= 0) {
                    // remove it
                    const newList = [...current];
                    newList.splice(index, 1);
                    return newList;
                }
                else {
                    // add it
                    return [...current, gameInfo];
                }
            });
        }
        return;
    }
    if (!browser || !FavoriteGame)
        return;
    try {
        const alreadyFavorited = get(favoriteGames).some((g) => g.provider_slug === providerSlug && g.game_slug === gameSlug);
        const res = await FavoriteGame({
            variables: { providerSlug, gameSlug, unfavorite: alreadyFavorited },
            context: { api: Api.player },
            refetchQueries: [{ query: GetMyGamesDoc }]
        });
        await refreshGamesState();
        return res;
    }
    catch (error) {
        console.log('Error favoriting game:', error);
    }
}
export async function addRecentlyPlayedGame(providerSlug, gameSlug, loggedIn) {
    if (!loggedIn) {
        const game = `${providerSlug}#${gameSlug}`;
        guestStorage.addGame('recently_played', game, realm);
        const gameInfo = await getGame(providerSlug, gameSlug);
        if (gameInfo) {
            recentlyPlayedGames.update((current) => {
                // Copy the current list.
                const newList = [...current];
                // If the game already exists, remove it first
                const index = newList.findIndex((g) => g.provider_slug === providerSlug && g.game_slug === gameSlug);
                if (index >= 0) {
                    newList.splice(index, 1);
                }
                // Add this game to the front
                newList.unshift(gameInfo);
                // Limit to 30 items
                if (newList.length > 30) {
                    newList.pop();
                }
                return newList;
            });
        }
        return;
    }
    // Logic for logged-in users if necessary
}
